import { useEffect, useState } from 'react'
import { Lock, Eye, Pencil, Zap, Power, ChevronRight, ShieldAlert, AlertTriangle } from 'lucide-react'
import { Link } from '../router.js'
import { apiGet, adminMutate, useToast } from './primitives.js'
import { invalidateAccessCaps } from '../access/lib.js'

/**
 * MODE SÛR V2 — le plafond de sécurité de TOUT Gestion, dans Réglages → Général.
 * 4 niveaux canoniques (DISABLED < READ_ONLY < EDIT < AUTOMATION). La liste des
 * intégrations vient EXCLUSIVEMENT du catalogue (SMTP/FCM/APNs déjà exclus).
 * Le mode CONFIGURÉ n'est jamais écrasé par l'effectif.
 */

type Mode = 'DISABLED' | 'READ_ONLY' | 'EDIT' | 'AUTOMATION'

interface IntegRow {
  key: string; name: string; family: string; kind: string; status: string; statusLabel: string
  applicable: boolean; configured: Mode | null; effective: Mode | null; max: Mode | null
  limitingFactor: 'GLOBAL_MODE' | 'INTEGRATION' | 'CAPABILITY' | 'ENVIRONMENT' | null
  editionAvailable: boolean; automationAvailable: boolean; configRoute: string | null
}
interface View { global: Mode; globalLabel: string; environmentCeiling: Mode; labels: Record<Mode, string>; integrations: IntegRow[]; affected: number }

const LEVELS: { mode: Mode; icon: typeof Lock; title: string; desc: string; risk: string }[] = [
  { mode: 'DISABLED', icon: Power, title: 'Désactivé', desc: 'Gestion n’échange plus de nouvelles données avec les intégrations.', risk: 'Coupe la synchronisation' },
  { mode: 'READ_ONLY', icon: Eye, title: 'Lecture seule', desc: 'Synchronisation et lecture autorisées. Aucune modification externe.', risk: 'Sûr' },
  { mode: 'EDIT', icon: Pencil, title: 'Édition', desc: 'Les utilisateurs autorisés peuvent effectuer des modifications manuelles.', risk: 'Écritures manuelles' },
  { mode: 'AUTOMATION', icon: Zap, title: 'Automatisations', desc: 'Les automatisations explicitement configurées et compatibles peuvent aussi écrire.', risk: 'Risque élevé' },
]

const FACTEUR: Record<string, string> = { GLOBAL_MODE: 'Mode sûr global', INTEGRATION: 'Configuration de l’intégration', CAPABILITY: 'Capacités de l’intégration', ENVIRONMENT: 'Plafond d’environnement' }

function ModePill({ mode, labels }: { mode: Mode | null; labels: Record<Mode, string> }) {
  if (!mode) return <span className="sm-pill sm-pill--na">Non applicable</span>
  const cls = mode === 'AUTOMATION' ? 'sm-pill--auto' : mode === 'EDIT' ? 'sm-pill--edit' : mode === 'DISABLED' ? 'sm-pill--off' : 'sm-pill--ro'
  return <span className={`sm-pill ${cls}`}>{labels[mode]}</span>
}

export function SafeModeSettings() {
  const toast = useToast()
  const [view, setView] = useState<View | null>(null)
  const [pending, setPending] = useState<Mode | null>(null)
  const [impact, setImpact] = useState<{ lines: { key: string; name: string; beforeLabel: string; afterLabel: string; changed: boolean }[]; willChange: number; automationCapable: number } | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const load = () => void apiGet<View>('/api/v1/settings/safe-mode').then(setView).catch(() => {})
  useEffect(load, [])
  // Deep-link /settings/general#safe-mode : on ancre proprement une fois chargé.
  useEffect(() => {
    if (view && typeof window !== 'undefined' && window.location.hash === '#safe-mode') {
      const t = setTimeout(() => document.getElementById('safe-mode')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
      return () => clearTimeout(t)
    }
    return undefined
  }, [view])

  const choose = async (m: Mode) => {
    if (!view || m === view.global) { setPending(null); setImpact(null); return }
    setPending(m)
    const imp = await apiGet<typeof impact>(`/api/v1/settings/safe-mode/impact?mode=${m}`)
    setImpact(imp)
  }
  const save = async () => {
    if (!pending) return
    setSaving(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/settings/safe-mode/global', { mode: pending, reason: reason || null })
    setSaving(false)
    if (r.status >= 400) { toast('error', r.data.message ?? r.data.error ?? 'Refusé.'); return }
    toast('ok', 'Mode sûr global enregistré.')
    invalidateAccessCaps() // les pages Access reflètent le nouveau mode sans reload (§10)
    setPending(null); setImpact(null); setReason(''); load()
  }

  if (!view) return <section className="lc-panel gd-panel" id="safe-mode" style={{ marginTop: 'var(--sp-5)' }}><div className="lc-panel__body"><p className="lc-hint">Chargement…</p></div></section>
  const shown = pending ?? view.global

  return (
    <section className="lc-panel gd-panel" id="safe-mode" style={{ marginTop: 'var(--sp-5)' }} aria-labelledby="sm-id">
      <header className="lc-panel__head">
        <h2 id="sm-id" className="lc-section-title">Mode sûr de Gestion</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-hint">{view.affected} intégration(s) gouvernée(s)</span>
      </header>
      <div className="lc-panel__body">
        <p className="sm-intro">Le plafond de sécurité opérationnelle de toutes les intégrations. Le mode EFFECTIF de chaque intégration est le plus restrictif entre ce plafond, sa propre configuration et ses capacités.</p>

        <div className="sm-levels" role="radiogroup" aria-label="Mode sûr global">
          {LEVELS.map((l) => (
            <button key={l.mode} type="button" role="radio" aria-checked={shown === l.mode} className={`sm-level ${shown === l.mode ? 'is-on' : ''} ${l.mode === 'AUTOMATION' ? 'sm-level--risk' : ''}`} onClick={() => choose(l.mode)}>
              <span className="sm-level__icon"><l.icon size={18} aria-hidden="true" /></span>
              <strong>{l.title}</strong>
              <span className="sm-level__desc">{l.desc}</span>
              <span className={`sm-level__risk ${l.mode === 'AUTOMATION' ? 'is-high' : ''}`}>{l.mode === 'AUTOMATION' ? <><ShieldAlert size={11} aria-hidden="true" /> {l.risk}</> : l.risk}</span>
            </button>
          ))}
        </div>

        {shown === 'AUTOMATION' ? (
          <p className="sm-automation-note"><AlertTriangle size={14} aria-hidden="true" /> Ce niveau n’active aucune automatisation à lui seul : il autorise les automatisations déjà développées, configurées et compatibles à écrire. Aucune intégration n’en dispose actuellement.</p>
        ) : null}

        {impact && pending ? (
          <div className="sm-impact">
            <h3>Impact du passage {view.labels[view.global]} → {view.labels[pending]}</h3>
            <ul className="sm-impact__lines">
              {impact.lines.filter((l) => l.changed).map((l) => (
                <li key={l.key}><span>{l.name}</span><span className="sm-impact__delta"><em>{l.beforeLabel}</em> → <strong>{l.afterLabel}</strong></span></li>
              ))}
              {impact.willChange === 0 ? <li className="lc-hint">Aucune intégration ne change de mode effectif (toutes limitées par leur configuration ou capacité).</li> : null}
            </ul>
            {pending === 'AUTOMATION' ? (
              <>
                <p className="lc-hint">{impact.automationCapable} intégration(s) disposent réellement d’automatisations d’écriture.</p>
                <label className="sm-reason">Motif (obligatoire)<textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Pourquoi lever le plafond à Automatisations ?" /></label>
              </>
            ) : null}
            <div className="sm-impact__actions">
              <button type="button" className="lc-btn" onClick={() => { setPending(null); setImpact(null) }}>Annuler</button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={saving || (pending === 'AUTOMATION' && !reason)} onClick={save}>Enregistrer le Mode sûr global</button>
            </div>
          </div>
        ) : null}

        <h3 className="sm-integ-title">Intégrations</h3>
        <div className="sm-integ-grid">
          {view.integrations.map((i) => {
            const card = (
              <>
                <div className="sm-integ__top">
                  <strong>{i.name}</strong>
                  {i.kind === 'LIVECLUB_SERVICE' ? <span className="sm-tag">Interne</span> : <span className="sm-tag sm-tag--tp">Tiers</span>}
                </div>
                <div className="sm-integ__status"><span className={`lc-dot ${i.status === 'OPERATIONNEL' ? 'lc-dot--ok' : 'lc-dot--idle'}`} aria-hidden="true" />{i.statusLabel}</div>
                {i.applicable ? (
                  <div className="sm-integ__modes">
                    <div><span className="sm-lbl">Configuré</span><ModePill mode={i.configured} labels={view.labels} /></div>
                    <div><span className="sm-lbl">Effectif</span><ModePill mode={i.effective} labels={view.labels} /></div>
                    {i.limitingFactor ? <div className="sm-integ__limit">Limité par : {FACTEUR[i.limitingFactor]}</div> : null}
                  </div>
                ) : <div className="sm-integ__na">Mode sûr non applicable (à intégrer)</div>}
                {i.configRoute ? <span className="sm-integ__cta">Configurer <ChevronRight size={14} aria-hidden="true" /></span> : null}
              </>
            )
            return i.configRoute
              ? <Link key={i.key} href={i.configRoute} className="sm-integ sm-integ--link">{card}</Link>
              : <div key={i.key} className="sm-integ">{card}</div>
          })}
        </div>
      </div>
    </section>
  )
}
